import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import dayjs, { Dayjs } from 'dayjs';
import { FiCalendar, FiChevronLeft, FiChevronRight, FiX } from 'react-icons/fi';
import './CustomDatePicker.css';

export interface CustomDatePickerProps {
  value?: string; // YYYY-MM-DD
  onChange: (date: string) => void;
  minDate?: string; // YYYY-MM-DD
  placeholder?: string;
  hasError?: boolean;
  disabled?: boolean;
  id?: string;
  name?: string;
}

const CustomDatePicker: React.FC<CustomDatePickerProps> = ({
  value = '',
  onChange,
  minDate,
  placeholder = 'Select date',
  hasError = false,
  disabled = false,
  id,
  name,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [viewMonth, setViewMonth] = useState<Dayjs>(() => {
    if (value && dayjs(value).isValid()) {
      return dayjs(value);
    }
    if (minDate && dayjs(minDate).isValid()) {
      return dayjs(minDate);
    }
    return dayjs();
  });

  const [popoverPos, setPopoverPos] = useState<{
    top?: number;
    bottom?: number;
    left?: number;
    right?: number;
  }>({});

  const triggerRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);

  // Sync viewMonth when value changes externally
  useEffect(() => {
    if (value && dayjs(value).isValid()) {
      setViewMonth(dayjs(value));
    }
  }, [value]);

  const updatePosition = useCallback(() => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();

    if (rect.bottom < 0 || rect.top > window.innerHeight) {
      setIsOpen(false);
      return;
    }

    const isSmall = window.innerWidth <= 1024 || window.innerHeight <= 600;
    const isMedium = window.innerWidth <= 1299 || window.innerHeight <= 799;
    const popoverWidth = isSmall ? 226 : isMedium ? 242 : 260;
    const popoverEstimatedHeight = isSmall ? 260 : isMedium ? 285 : 315;

    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;

    const openUpwards = spaceBelow < popoverEstimatedHeight && spaceAbove > spaceBelow;

    let left: number | undefined = undefined;
    let right: number | undefined = undefined;

    if (rect.left + popoverWidth <= window.innerWidth - 8) {
      left = Math.max(8, rect.left);
    } else if (rect.right - popoverWidth >= 8) {
      right = Math.max(8, window.innerWidth - rect.right);
    } else {
      left = Math.max(8, Math.min(rect.left, window.innerWidth - popoverWidth - 8));
    }

    if (openUpwards) {
      setPopoverPos({
        bottom: Math.max(8, window.innerHeight - rect.top + 4),
        left,
        right,
      });
    } else {
      setPopoverPos({
        top: rect.bottom + 4,
        left,
        right,
      });
    }
  }, []);

  useEffect(() => {
    if (!isOpen) return;

    updatePosition();

    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        triggerRef.current && !triggerRef.current.contains(target) &&
        popoverRef.current && !popoverRef.current.contains(target)
      ) {
        setIsOpen(false);
      }
    };

    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);
    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, updatePosition]);

  // Calendar days grid calculation
  const calendarDays = useMemo(() => {
    const startOfMonth = viewMonth.startOf('month');
    const startDayOfWeek = startOfMonth.day(); // 0 is Sunday
    const daysInMonth = viewMonth.daysInMonth();

    const days: Array<Dayjs | null> = [];

    // Empty cells before start of month
    for (let i = 0; i < startDayOfWeek; i++) {
      days.push(null);
    }

    // Days of current month
    for (let d = 1; d <= daysInMonth; d++) {
      days.push(viewMonth.date(d));
    }

    return days;
  }, [viewMonth]);

  const handleSelectDate = (dateStr: string) => {
    onChange(dateStr);
    setIsOpen(false);
  };

  const handleClear = () => {
    onChange('');
    setIsOpen(false);
  };

  const isPrevDisabled = useMemo(() => {
    if (!minDate) return false;
    const minMonth = dayjs(minDate).startOf('month');
    return viewMonth.startOf('month').isSame(minMonth) || viewMonth.startOf('month').isBefore(minMonth);
  }, [minDate, viewMonth]);

  const triggerFormattedText = useMemo(() => {
    if (!value || !dayjs(value).isValid()) return '';
    return dayjs(value).format('DD MMM YYYY');
  }, [value]);

  return (
    <div className="cdp-picker-wrapper">
      <button
        ref={triggerRef}
        type="button"
        id={id}
        name={name}
        disabled={disabled}
        className={`cdp-trigger-btn ${isOpen ? 'active-popover' : ''} ${hasError ? 'has-error' : ''}`}
        onClick={() => {
          if (disabled) return;
          if (!isOpen) {
            updatePosition();
          }
          setIsOpen(!isOpen);
        }}
      >
        <span className="cdp-trigger-left">
          <FiCalendar className="cdp-icon-cal" size={15} />
          <span className={`cdp-trigger-text ${!triggerFormattedText ? 'placeholder' : ''}`}>
            {triggerFormattedText || placeholder}
          </span>
        </span>

        {triggerFormattedText && !disabled && (
          <span
            role="button"
            tabIndex={0}
            className="cdp-clear-btn"
            onClick={(e) => {
              e.stopPropagation();
              handleClear();
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.stopPropagation();
                handleClear();
              }
            }}
            title="Clear date"
          >
            <FiX size={13} />
          </span>
        )}
      </button>

      {isOpen &&
        createPortal(
          <div
            ref={popoverRef}
            className="cdp-popover"
            style={{
              position: 'fixed',
              top: popoverPos.top,
              bottom: popoverPos.bottom,
              left: popoverPos.left,
              right: popoverPos.right,
              zIndex: 999999,
            }}
            onMouseDown={(e) => e.stopPropagation()}
          >
            {/* Quick Presets for Deadlines */}
            <div className="cdp-presets-row">
              <button
                type="button"
                className="cdp-preset-pill"
                onClick={() => handleSelectDate(dayjs().add(7, 'day').format('YYYY-MM-DD'))}
              >
                In 7 Days
              </button>
              <button
                type="button"
                className="cdp-preset-pill"
                onClick={() => handleSelectDate(dayjs().add(15, 'day').format('YYYY-MM-DD'))}
              >
                In 15 Days
              </button>
              <button
                type="button"
                className="cdp-preset-pill"
                onClick={() => handleSelectDate(dayjs().add(30, 'day').format('YYYY-MM-DD'))}
              >
                In 30 Days
              </button>
              <button
                type="button"
                className="cdp-preset-pill"
                onClick={() => handleSelectDate(dayjs().endOf('month').format('YYYY-MM-DD'))}
              >
                End of Month
              </button>
            </div>

            {/* Month Header Switcher */}
            <div className="cdp-month-header">
              <button
                type="button"
                className="cdp-nav-btn"
                disabled={isPrevDisabled}
                onClick={() => setViewMonth(viewMonth.subtract(1, 'month'))}
                title="Previous month"
              >
                <FiChevronLeft size={15} />
              </button>

              <div className="cdp-month-title">
                {viewMonth.format('MMMM YYYY')}
              </div>

              <button
                type="button"
                className="cdp-nav-btn"
                onClick={() => setViewMonth(viewMonth.add(1, 'month'))}
                title="Next month"
              >
                <FiChevronRight size={15} />
              </button>
            </div>

            {/* Weekdays */}
            <div className="cdp-days-header">
              <div className="cdp-day-name">Su</div>
              <div className="cdp-day-name">Mo</div>
              <div className="cdp-day-name">Tu</div>
              <div className="cdp-day-name">We</div>
              <div className="cdp-day-name">Th</div>
              <div className="cdp-day-name">Fr</div>
              <div className="cdp-day-name">Sa</div>
            </div>

            {/* Days Grid */}
            <div className="cdp-days-grid">
              {calendarDays.map((dateObj, idx) => {
                if (!dateObj) {
                  return <div key={`empty-${idx}`} className="cdp-day-cell empty" />;
                }

                const formattedStr = dateObj.format('YYYY-MM-DD');
                const isSelected = value === formattedStr;
                const isToday = formattedStr === dayjs().format('YYYY-MM-DD');
                const isDisabled = Boolean(minDate && dateObj.isBefore(dayjs(minDate), 'day'));

                return (
                  <button
                    key={formattedStr}
                    type="button"
                    disabled={isDisabled}
                    className={`cdp-day-cell ${isSelected ? 'selected' : ''} ${isToday ? 'today' : ''} ${isDisabled ? 'disabled' : ''}`}
                    onClick={() => handleSelectDate(formattedStr)}
                  >
                    {dateObj.date()}
                  </button>
                );
              })}
            </div>

            {/* Footer */}
            <div className="cdp-footer">
              <div className="cdp-footer-summary">
                {triggerFormattedText || 'No date selected'}
              </div>

              <div className="cdp-footer-actions">
                <button
                  type="button"
                  className="btn-cdp-today"
                  onClick={() => handleSelectDate(dayjs().format('YYYY-MM-DD'))}
                >
                  Today
                </button>
                {value && (
                  <button
                    type="button"
                    className="btn-cdp-clear"
                    onClick={handleClear}
                  >
                    Clear
                  </button>
                )}
              </div>
            </div>
          </div>,
          document.body
        )}
    </div>
  );
};

export default CustomDatePicker;
